<template>
  <v-dialog v-model="show" persistent max-width="700px">
    <v-card :class="classContents">
      <v-card-title>
        <h3>{{ title }}</h3>
      </v-card-title>
      <v-form ref="form" v-model="valid">
        <v-row class="mx-3 mt-3">
          <v-col cols="6" class="pr-5">
            <div class="my-1">
              <div class="float-left column-name">
                <span class="d-flex align-center">{{ $t('datasource.name') }}</span>
              </div>
              <div class="float-left column-contents">
                <v-text-field v-model="item.name" :disabled="readonly" :rules="[rules.required]" class="w-auto" outlined dense></v-text-field>
              </div>
            </div>
          </v-col>
          <v-col cols="6" class="pr-5">
            <div class="my-1">
              <div class="float-left column-name">
                <span class="d-flex align-center">{{ $t('datasource.maximumPoolSize') }}</span>
              </div>
              <div class="float-left column-contents">
                <v-text-field v-model="item.maximumPoolSize" type="number" min="1" :rules="[rules.required]" class="w-auto" outlined dense></v-text-field>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row class="mx-3 mt-3">
          <v-col cols="6" class="pr-5">
            <div class="my-1">
              <div class="float-left column-name">
                <span class="d-flex align-center">{{ $t('datasource.username') }}</span>
              </div>
              <div class="float-left column-contents">
                <v-text-field v-model="item.username" :rules="[rules.required]" class="w-auto" outlined dense></v-text-field>
              </div>
            </div>
          </v-col>
          <v-col cols="6" class="pr-5">
            <div class="my-1">
              <div class="float-left column-name">
                <span class="d-flex align-center">{{ $t('datasource.password') }}</span>
              </div>
              <div class="float-left column-contents">
                <v-text-field v-model="item.password" :rules="[rules.required]" class="w-auto" outlined dense></v-text-field>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row class="mx-3 mb-3">
          <v-col cols="12" class="pr-5">
            <v-card flat class="dialog-form" :class="classContents">
              <div class="d-flex flex-column">
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ this.$t('datasource.jdbcUrl') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-text-field v-model="item.jdbcUrl" :rules="[rules.required]" class="w-auto" outlined dense></v-text-field>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <v-row class="mx-3 mt-3">
          <v-col cols="12" class="pr-5">
            <v-card flat class="dialog-form" :class="classContents">
              <div class="d-flex flex-column">
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ $t('datasource.targets') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <!-- eslint-disable-next-line prettier/prettier -->
                    <v-select v-model="item.targets" :rules="[rules.required]" :items="systems" item-text="alias" item-value="id" class="item-select w-auto"
                      :placeholder="this.$t('common.noSelect')" :menu-props="{ offsetY: true }" multiple outlined dense
                      :style="{ width: '500px' }"
                    ></v-select>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn small color="primary" @click="test" :loading="loadingTest" :disabled="disabledTest">{{ $t('datasource.testconnection') }}</v-btn>
        <v-btn small color="primary" @click="save" :loading="loadingSave" :disabled="disabledSave">{{ $t('common.save') }}</v-btn>
        <v-btn small color="primary" @click="cancel">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { api } from '@/api/common/instances';
import validation from '@/utils/validation';
import { isEqual, merge } from 'lodash';

const defaultItem = {
  name: '',
  maximumPoolSize: '',
  username: '',
  password: '',
  jdbcUrl: '',
  targets: '',
};

let beforeChangeItem = {};

export default {
  props: {
    values: {
      type: Object,
      required: true,
    },
    value: Boolean,
  },
  data() {
    return {
      title: '',
      mounted: false,
      item: Object.assign({}, defaultItem),
      valid: true,
      readonly: false,
      loadingTest: false,
      loadingSave: false,
      testConnection: {
        success: false,
        jdbcUrl: '',
        username: '',
        password: '',
      },
      systems: [],
      rules: {
        ...validation,
      },
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    disabledTest() {
      return !this.item.jdbcUrl && !this.item.username && !this.item.password;
    },
    disabledSave() {
      if (beforeChangeItem.name) {
        return !this.valid || isEqual(beforeChangeItem, this.item);
      } else {
        return !this.valid && !this.testConnection.success;
      }
    },
    classContents() {
      return {
        'contents-background-dark': this.$vuetify.theme.isDark,
      };
    },
  },
  watch: {
    show: {
      immediate: true,
      handler: 'setDialogActivated',
    },
    'item.jdbcUrl': {
      handler: 'setConnectionSuccessStatus',
    },
    'item.username': {
      handler: 'setConnectionSuccessStatus',
    },
    'item.password': {
      handler: 'setConnectionSuccessStatus',
    },
  },
  methods: {
    async save() {
      this.loadingSave = true;
      const { status } = this.values.name ? await this.update() : await this.add();
      if (status == 200) {
        this.show = false;
        if (!beforeChangeItem.name) this.$emit('insert', this.item);
      }
      this.loadingSave = false;
    },
    async test() {
      const enc = s => encodeURIComponent(s);
      const response = await api.get(`/preference/datasource/test?jdbcUrl=${enc(this.item.jdbcUrl)}&username=${enc(this.item.username)}&password=${enc(this.item.password)}`);
      if (response.status == 200) {
        this.$dialog.info(this.$t('datasource.testconnection.success.i'));
        this.testConnection.success = true;
        this.testConnection.jdbcUrl = this.item.jdbcUrl;
        this.testConnection.username = this.item.username;
        this.testConnection.password = this.item.password;
      } else {
        this.testConnection.success = false;
        this.testConnection.jdbcUrl = '';
        this.testConnection.username = '';
        this.testConnection.password = '';
      }
    },
    async add() {
      return await api.post('/preference/datasources', this.item);
    },
    async update() {
      return await api.put(`/preference/datasources/${this.item.name}`, this.item);
    },
    cancel() {
      merge(this.item, beforeChangeItem);
      beforeChangeItem = {};
      this.show = false;
    },
    setConnectionSuccessStatus() {
      this.testConnection.success = this.testConnection.jdbcUrl == this.item.jdbcUrl && this.testConnection.username == this.item.username && this.testConnection.password == this.item.password;
    },
    async setDialogActivated(show) {
      if (show) {
        if (this.values.name) {
          this.title = this.$t('datasource.title.edit');
          this.readonly = true;
          beforeChangeItem = merge({}, this.values);
          this.item = this.values;
        } else {
          this.title = this.$t('datasource.title.add');
          this.readonly = false;
          beforeChangeItem = merge({}, defaultItem);
          this.item = merge({}, defaultItem);
          this.onMounted().then(() => this.$refs.form.resetValidation());
        }
      } else {
        this.item = merge({}, defaultItem);
        this.$refs.form.resetValidation();
      }
    },
    onMounted() {
      return new Promise(resolve => {
        const interval = setInterval(() => {
          if (this.mounted) {
            clearInterval(interval);
            resolve();
          }
        }, 100);
      });
    },
  },
  async created() {
    ({ data: this.systems } = await api.get('/preference/systems'));
  },
  mounted() {
    this.mounted = true;
  },
};
</script>

<style scoped>
.empty {
  height: 50px;
}
</style>
<style>
.item-select .v-input__slot {
  height: 24px;
}
.v-input__append-inner {
  margin-top: 2px !important;
}
.v-input__prepend-inner {
  margin-top: 2px !important;
}
</style>
